import React from "react";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import {
    Box, Button, Chip, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography
} from "@mui/material";
import {Add} from "@mui/icons-material";
import {useGetSponsoredNotifications} from "../../../services/ContentManager";
import CircularLoading from "../../../components/CircularLoading";
import {SPONSORED_NOTIFICATION_CREATE_ROUTE} from "../../../constants/vendors";
import {COST_PER_READ} from "./SponsoredNotificationCreate";

/** Stato di moderazione, con l'attivazione come sfumatura di quelle approvate. */
function statoNotifica(n, t) {
    if (n.status === 'REJECTED') return {label: t('notificationRejected'), color: 'error'};
    if (n.status !== 'APPROVED') return {label: t('notificationPending'), color: 'warning'};
    return n.active
        ? {label: t('notificationSent'), color: 'success'}
        : {label: t('notificationApproved'), color: 'default'};
}

export default function SponsoredNotificationsList({vendor}) {
    const {t} = useTranslation();
    const history = useHistory();
    const {notifications, status} = useGetSponsoredNotifications();

    const balance = vendor?.points != null ? parseFloat(vendor.points) : 0;

    if (status === 'loading') return <CircularLoading/>;

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">{t('sponsoredNotifications')}</Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                        label={`${t('balance')}: ${balance} ${t('points')}`}
                        color="primary"
                        variant="outlined"
                        sx={{fontSize: '1rem', py: 2, px: 1}}
                    />
                    <Button
                        variant="contained"
                        startIcon={<Add/>}
                        onClick={() => history.push(SPONSORED_NOTIFICATION_CREATE_ROUTE)}
                    >
                        {t('newSponsoredNotification')}
                    </Button>
                </Box>
            </Box>

            <Paper variant="outlined" sx={{p: 2, mb: 3}}>
                <Typography variant="h5" fontWeight={700}>{t('sponsoredNotificationsIntroTitle')}</Typography>
                <Typography variant="h6" mb={1}>{t('sponsoredNotificationsIntroSubtitle', {n: COST_PER_READ})}</Typography>
                <Typography variant="body2" color="textSecondary">
                    {vendor?.notificationsAutoApproved
                        ? t('notificationSendsWithoutReview')
                        : t('notificationWillBeReviewed')}
                </Typography>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('notificationTitle')}</TableCell>
                            <TableCell>{t('targetType')}</TableCell>
                            <TableCell>{t('status')}</TableCell>
                            <TableCell>{t('actualSent')}</TableCell>
                            <TableCell>{t('totalReads')}</TableCell>
                            <TableCell>{t('totalCost')}</TableCell>
                            <TableCell>{t('date')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notifications.map((n) => (
                            <TableRow key={n.id} hover sx={{cursor: 'pointer'}}
                                      onClick={() => history.push(SPONSORED_NOTIFICATION_CREATE_ROUTE + n.id)}>
                                <TableCell>{n.title}</TableCell>
                                <TableCell>{n.targetType === 'GEO_RADIUS' ? t('geoRadius') : t('allUsers')}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={statoNotifica(n, t).label}
                                        color={statoNotifica(n, t).color}
                                        size="small"
                                    />
                                    {n.moderationNote &&
                                        <Typography variant="caption" display="block" color="textSecondary">
                                            {n.moderationNote}
                                        </Typography>}
                                </TableCell>
                                <TableCell>{n.actualSent || 0}</TableCell>
                                <TableCell>{n.totalReads || 0}</TableCell>
                                <TableCell>{`${n.totalCost ? parseFloat(n.totalCost) : 0} ${t('points')}`}</TableCell>
                                <TableCell>{n.createdDate ? new Date(n.createdDate).toLocaleDateString('it-IT') : '-'}</TableCell>
                            </TableRow>
                        ))}
                        {notifications.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography color="textSecondary" sx={{py: 3}}>
                                        {t('vendorNoSponsoredNotifications')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
